import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HardHat, FileText } from 'lucide-react';
import { EmptyState } from '@/components/system/EmptyState';
import type { ExecucaoNaoMedidaResumo } from '../hooks/useIFExecucoesNaoMedidas';
import type { BoletimStatusResumo } from '../hooks/useIFBoletinsPorStatus';

import { fmtCurrencyInt } from '@/lib/formatters';
const fmtBRL = fmtCurrencyInt;

/** Short BRL formatter for large values: R$ 1,2M / R$ 3,4B */
export const fmtBRLShort = (v: number): string => {
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}R$ ${(abs / 1_000_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}B`;
  if (abs >= 1_000_000) return `${sign}R$ ${(abs / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1_000) return `${sign}R$ ${(abs / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}k`;
  return fmtCurrencyInt(v);
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  aprovado_para_faturamento: { label: 'Aprovado', color: 'bg-emerald-500' },
  rascunho: { label: 'Rascunho', color: 'bg-muted-foreground/40' },
  em_revisao: { label: 'Em Revisão', color: 'bg-amber-500' },
  pendente: { label: 'Pendente', color: 'bg-amber-500' },
  rejeitado: { label: 'Rejeitado', color: 'bg-destructive' },
  desconhecido: { label: 'Outro', color: 'bg-muted-foreground/30' },
};

function getStatusMeta(status: string) {
  return STATUS_LABELS[status] || { label: status.replace(/_/g, ' '), color: 'bg-muted-foreground/30' };
}

// ── Execuções Não Medidas Card ───────────────────────────────────────────────

interface ExecNaoMedidaCardProps {
  data: ExecucaoNaoMedidaResumo[] | null | undefined;
  isLoading?: boolean;
  single?: ExecucaoNaoMedidaResumo | null;
  /** When true, renders without Card wrapper (for embedding) */
  embedded?: boolean;
}

function ExecNaoMedidaInner({ data, isLoading, single }: Omit<ExecNaoMedidaCardProps, 'embedded'>) {
  const totalValor = single
    ? single.valor_estimado
    : (data || []).reduce((s, d) => s + d.valor_estimado, 0);
  const totalRegistros = single
    ? single.total_registros
    : (data || []).reduce((s, d) => s + d.total_registros, 0);

  return (
    <>
      <div className="flex items-center gap-2 text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
        <HardHat className="h-3.5 w-3.5" />
        Exec. Não Medidas
      </div>
      <p className="text-xl font-bold mt-1.5 leading-none tabular-nums font-display">
        {isLoading ? '…' : fmtBRL(totalValor)}
      </p>
      <p className="text-[11px] text-muted-foreground/70 mt-1">
        {isLoading ? '' : `${totalRegistros} registro(s) sem boletim`}
      </p>
    </>
  );
}

export const ExecucoesNaoMedidasCard: React.FC<ExecNaoMedidaCardProps> = ({ embedded, ...props }) => {
  if (embedded) return <div><ExecNaoMedidaInner {...props} /></div>;
  return (
    <Card className="border-border/40">
      <CardContent className="pt-5 pb-4">
        <ExecNaoMedidaInner {...props} />
      </CardContent>
    </Card>
  );
};

// ── Boletins por Status Card ─────────────────────────────────────────────────

interface BoletinsPorStatusCardProps {
  dataByContrato?: { contrato_id: string; resumos: BoletimStatusResumo[] }[] | null;
  resumos?: BoletimStatusResumo[] | null;
  isLoading?: boolean;
  embedded?: boolean;
}

function BoletinsInner({ dataByContrato, resumos, isLoading }: Omit<BoletinsPorStatusCardProps, 'embedded'>) {
  const aggMap = new Map<string, { label: string; color: string; valor: number; count: number }>();
  const items = resumos ? resumos : (dataByContrato || []).flatMap(d => d.resumos);

  for (const r of items) {
    const meta = getStatusMeta(r.status);
    const agg = aggMap.get(r.status) || { ...meta, valor: 0, count: 0 };
    agg.valor += r.valor_liquido;
    agg.count += r.total_boletins;
    aggMap.set(r.status, agg);
  }

  const entries = Array.from(aggMap.values()).sort((a, b) => b.valor - a.valor);
  const totalValor = entries.reduce((s, e) => s + e.valor, 0);

  return (
    <>
      <div className="flex items-center gap-2 text-muted-foreground text-[10px] font-semibold tracking-wider uppercase mb-2.5">
        <FileText className="h-3.5 w-3.5" />
        Boletins por Status
      </div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : entries.length === 0 ? (
        <EmptyState text="Nenhum boletim registrado neste contrato." icon={FileText} className="py-4" />
      ) : (
        <>
          <div className="flex h-2 rounded-full overflow-hidden mb-2.5">
            {entries.map(e => {
              const pct = totalValor > 0 ? (e.valor / totalValor) * 100 : 0;
              return pct > 0 ? (
                <div key={e.label} className={`${e.color} transition-all`} style={{ width: `${pct}%` }} />
              ) : null;
            })}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
            {entries.map(e => (
              <div key={e.label} className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${e.color}`} />
                <span className="text-[11px] text-muted-foreground">{e.label}</span>
                <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
                  {fmtBRL(e.valor)}
                </Badge>
                <span className="text-[10px] text-muted-foreground/50">({e.count})</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

export const BoletinsPorStatusCard: React.FC<BoletinsPorStatusCardProps> = ({ embedded, ...props }) => {
  if (embedded) return <div><BoletinsInner {...props} /></div>;
  return (
    <Card className="border-border/40">
      <CardContent className="pt-5 pb-4">
        <BoletinsInner {...props} />
      </CardContent>
    </Card>
  );
};
